import React, { useState, useRef, useEffect } from 'react';
import { Box, Paper, Typography, Tabs, Tab, IconButton, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, CircularProgress, ListItemIcon, Breadcrumbs, Menu, MenuItem, Tooltip } from '@mui/material';
import MonacoEditor from '@monaco-editor/react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import { backendUrl, API_KEYS } from '../config';

const defaultFile = () => ({
  name: 'note.txt',
  language: 'plaintext',
  content: '',
  backendPath: null, // chemin backend si ouvert depuis explorer
});

export default function NotepadApp({ filePath }) {
  const [files, setFiles] = useState([defaultFile()]);
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = useRef();
  // Explorer backend
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [explorerPath, setExplorerPath] = useState('/');
  const [explorerFiles, setExplorerFiles] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState('');
  // Sauvegarde backend
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savePath, setSavePath] = useState('/');
  const [saveName, setSaveName] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveExplorerFiles, setSaveExplorerFiles] = useState([]);
  // Création fichier/dossier
  const [explorerNewDialog, setExplorerNewDialog] = useState({ open: false, type: 'file', value: '' });

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const handleEditorChange = (value) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      newFiles[activeTab].content = value || '';
      return newFiles;
    });
  };

  const handleAddTab = () => {
    setFiles((prev) => [...prev, defaultFile()]);
    setActiveTab(files.length);
  };

  const handleTabClose = (index) => {
    if (files.length === 1) return; // Always keep at least one tab
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    setActiveTab((prev) => (index === prev ? 0 : prev > index ? prev - 1 : prev));
  };

  const handleFileNameChange = (e) => {
    const newName = e.target.value;
    setFiles((prev) => {
      const newFiles = [...prev];
      newFiles[activeTab].name = newName;
      return newFiles;
    });
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setFiles((prev) => {
      const newFiles = [...prev];
      newFiles[activeTab].language = newLang;
      return newFiles;
    });
  };

  const handleExport = () => {
    const file = files[activeTab];
    const blob = new Blob([file.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenFileClick = () => {
    fileInputRef.current.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setFiles((prev) => [
        ...prev,
        {
          name: file.name,
          language: 'plaintext',
          content,
        },
      ]);
      setActiveTab(files.length);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const languageOptions = [
    { value: 'plaintext', label: 'Texte' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
    { value: 'json', label: 'JSON' },
    { value: 'html', label: 'HTML' },
    { value: 'css', label: 'CSS' },
    { value: 'markdown', label: 'Markdown' },
  ];

  // ----------- OUVERTURE DEPUIS EXPLORER (BACKEND) -----------
  const handleOpenFromExplorer = () => {
    setExplorerOpen(true);
    setExplorerPath('/');
    fetchExplorerFiles('/');
  };

  const fetchExplorerFiles = async (path) => {
    setExplorerLoading(true);
    setExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`${backendUrl}/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExplorerFiles(res.data);
    } catch (e) {
      setExplorerError("Erreur lors du chargement des fichiers.");
      setExplorerFiles([]);
    } finally {
      setExplorerLoading(false);
    }
  };

  const handleExplorerNavigate = (folder) => {
    const newPath = explorerPath === '/' ? `/${folder}` : `${explorerPath}/${folder}`;
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath);
  };

  const handleExplorerGoUp = () => {
    if (explorerPath === '/') return;
    const parts = explorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath);
  };

  const handleExplorerSelectFile = async (file) => {
    // Lecture backend
    try {
      setExplorerLoading(true);
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
      // Utilise la nouvelle route GET /download-file pour lire le fichier en texte brut
      const url = `${backendUrl}/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error('Erreur lors du téléchargement du fichier');
      let fileContent = await res.text();
      setFiles((prev) => [
        ...prev,
        {
          name: file.name,
          language: 'plaintext',
          content: fileContent,
          backendPath: relPath,
        },
      ]);
      setActiveTab(files.length);
      setExplorerOpen(false);
    } catch (e) {
      setExplorerError("Erreur lors de la lecture du fichier.");
    } finally {
      setExplorerLoading(false);
    }
  };

  // Création fichier/dossier
  const handleExplorerNew = (type) => setExplorerNewDialog({ open: true, type, value: '' });
  const handleExplorerNewValidate = async () => {
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      let relPath = explorerPath === '/' ? explorerNewDialog.value : explorerPath.replace(/^\//, '') + '/' + explorerNewDialog.value;
      if (explorerNewDialog.type === 'folder') {
        await api.post('/create-folder', { uuid7, path: relPath });
      } else {
        await api.post('/create-file', { uuid7, path: relPath });
      }
      setExplorerNewDialog({ open: false, type: 'file', value: '' });
      fetchExplorerFiles(explorerPath);
    } catch (e) {
      setExplorerError("Erreur lors de la création.");
    }
  };
  const handleExplorerNewCancel = () => setExplorerNewDialog({ open: false, type: 'file', value: '' });

  // ----------- ENREGISTRER SUR L'EXPLORER (BACKEND) -----------
  const handleSaveToExplorer = () => {
    // Si le fichier a déjà backendPath, on écrase
    const file = files[activeTab];
    if (file.backendPath) {
      saveFileToBackend(file.backendPath, file.content);
    } else {
      setSaveDialogOpen(true);
      setSavePath('/');
      setSaveName(file.name);
    }
  };

  const saveFileToBackend = async (path, content) => {
  setSaveLoading(true);
  setSaveError('');
  try {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
    }
    if (!uuid7) throw new Error('Utilisateur non trouvé');
    const token = window.localStorage.getItem('token');
    // Crée un blob à partir du contenu texte
    const fileName = files[activeTab]?.name || 'note.txt';
    const blob = new Blob([content], { type: 'text/plain' });
    const formData = new FormData();
    formData.append('uuid7', uuid7);
    formData.append('path', path.replace(/^\//, ''));
    formData.append('file', blob, fileName);
    await fetch(`${backendUrl}/upload-file`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    // Marquer l'onglet comme lié à ce chemin backend
    setFiles((prev) => {
      const newFiles = [...prev];
      newFiles[activeTab].backendPath = path;
      return newFiles;
    });
    setSaveDialogOpen(false);
  } catch (e) {
    setSaveError("Erreur lors de la sauvegarde.");
  } finally {
    setSaveLoading(false);
  }
};

  const handleSaveDialogValidate = () => {
    let relPath = savePath === '/' ? saveName : savePath.replace(/^\//, '') + '/' + saveName;
    saveFileToBackend(relPath, files[activeTab].content);
  };

  const fetchSaveExplorerFiles = async (path) => {
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveExplorerFiles(res.data.filter(f => f.type === 'folder' || f.type === 'file'));
    } catch {
      setSaveExplorerFiles([]);
    }
  };

  useEffect(() => {
    if (saveDialogOpen) fetchSaveExplorerFiles(savePath);
  }, [saveDialogOpen, savePath]);

  const handleSaveExplorerNavigate = (folder) => {
    const newPath = savePath === '/' ? `/${folder}` : `${savePath}/${folder}`;
    setSavePath(newPath);
  };

  const handleSaveExplorerNew = async (type) => {
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      let relPath = savePath === '/' ? '' : savePath.replace(/^\//, '');
      relPath = relPath ? relPath + '/' : '';
      const name = window.prompt('Nom du dossier :');
      if (!name) return;
      if (type === 'folder') {
        await api.post('/create-folder', { uuid7, path: relPath + name });
      }
      fetchSaveExplorerFiles(savePath);
    } catch {}
  };

  // --- useEffect pour ouverture directe d'un fichier depuis filePath (explorer) ---
  useEffect(() => {
    async function openFileFromProps() {
      if (typeof filePath === 'string' && filePath.length > 0) {
        try {
          setExplorerLoading(true);
          setExplorerError('');
          // Récupère l'utilisateur pour uuid7
          let uuid7 = null;
          try {
            const userStr = window.localStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              uuid7 = user.uuid7 || user.uuid || user.id || null;
            }
          } catch (e) {}
          if (!uuid7) throw new Error("Impossible de trouver l'identifiant utilisateur (uuid7)");
          const relPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
          const res = await fetch(`${backendUrl}/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
            headers: {
              'x-api-key': API_KEYS.key,
              'x-api-secret': API_KEYS.secret,
            }
          });
          if (!res.ok) throw new Error("Erreur lors du chargement du fichier");
          const data = await res.json();
          if (typeof data.content !== 'string') throw new Error("Réponse inattendue du backend");
          setFiles([{ name: relPath.split('/').pop() || 'note.txt', language: 'plaintext', content: data.content, backendPath: relPath }]);
          setActiveTab(0);
        } catch (e) {
          setExplorerError(e.message || "Erreur lors de l'ouverture du fichier depuis l'explorateur");
        } finally {
          setExplorerLoading(false);
        }
      }
    }
    if (typeof filePath !== 'undefined') {
      openFileFromProps();
    }
    // eslint-disable-next-line
  }, [filePath]);

  // --- useEffect pour ouverture directe d'un fichier depuis localStorage (fallback) ---
  useEffect(() => {
    let effectiveFilePath = filePath;
    if (!effectiveFilePath) {
      // Fallback : lire depuis localStorage si présent
      const stored = window.localStorage.getItem('notepadApp_filePath');
      if (stored) {
        effectiveFilePath = stored;
        window.localStorage.removeItem('notepadApp_filePath');
      }
    }
    async function openFileFromStorage() {
      if (typeof effectiveFilePath === 'string' && effectiveFilePath.length > 0) {
        try {
          setExplorerLoading(true);
          setExplorerError('');
          let uuid7 = null;
          try {
            const userStr = window.localStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              uuid7 = user.uuid7 || user.uuid || user.id || null;
            }
          } catch (e) {}
          if (!uuid7) throw new Error("Impossible de trouver l'identifiant utilisateur (uuid7)");
          const backendUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
          const relPath = effectiveFilePath.startsWith('/') ? effectiveFilePath.slice(1) : effectiveFilePath;
          const res = await fetch(`${backendUrl}/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
            headers: {
              'x-api-key': process.env.REACT_APP_API_KEY,
              'x-api-secret': process.env.REACT_APP_API_SECRET,
            }
          });
          if (!res.ok) throw new Error("Erreur lors du chargement du fichier");
          const data = await res.json();
          if (typeof data.content !== 'string') throw new Error("Réponse inattendue du backend");
          setFiles([{ name: relPath.split('/').pop() || 'note.txt', language: 'plaintext', content: data.content, backendPath: relPath }]);
          setActiveTab(0);
        } catch (e) {
          setExplorerError(e.message || "Erreur lors de l'ouverture du fichier depuis l'explorateur");
        } finally {
          setExplorerLoading(false);
        }
      }
    }
    if (typeof effectiveFilePath !== 'undefined' && effectiveFilePath) {
      openFileFromStorage();
    }
    // eslint-disable-next-line
  }, [filePath]);

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', p: 0, m: 0 }}>
      <Paper elevation={2} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', p: 3, m: 0, flex: 1, minHeight: 0, minWidth: 0, bgcolor: '#f7f9fa' }}>
        <Typography variant="h5" gutterBottom>Notepad++ Web</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Tooltip title="Importer">
            <IconButton color="primary" onClick={handleOpenFileClick} size="large">
              <UploadFileIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Exporter">
            <IconButton color="primary" onClick={handleExport} size="large">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ouvrir">
            <IconButton color="secondary" onClick={handleOpenFromExplorer} size="large">
              <FolderOpenIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enregistrer">
            <IconButton color="success" onClick={handleSaveToExplorer} size="large">
              <SaveIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            {files.map((file, idx) => (
              <Tab
                key={idx}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {file.name}
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleTabClose(idx); }}
                      tabIndex={-1}
                      style={{ pointerEvents: 'auto' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
                component="div"
              />
            ))}
            <Tab icon={<AddIcon />} onClick={handleAddTab} />
          </Tabs>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: 2, p: 2, boxShadow: 1 }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2 }}>
            <TextField
              label="Nom du fichier"
              value={files[activeTab].name}
              onChange={handleFileNameChange}
              size="medium"
              sx={{ width: 250, fontSize: 18 }}
              InputLabelProps={{ style: { fontSize: 16 } }}
              InputProps={{ style: { fontSize: 18, fontWeight: 500, background: '#fafdff' } }}
            />
            <TextField
              select
              label="Langage"
              value={files[activeTab].language}
              onChange={handleLanguageChange}
              size="medium"
              SelectProps={{ native: true }}
              sx={{ width: 180, fontSize: 18 }}
              InputLabelProps={{ style: { fontSize: 16 } }}
              InputProps={{ style: { fontSize: 18, background: '#fafdff' } }}
            >
              {languageOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', boxShadow: 1, bgcolor: '#fff' }}>
            <MonacoEditor
              height="100%"
              width="100%"
              language={files[activeTab].language}
              value={files[activeTab].content}
              onChange={handleEditorChange}
              theme="vs"
              options={{ fontSize: 16, minimap: { enabled: false } }}
            />
          </Box>
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.js,.json,.md,.html,.css,.py,.csv,.log,.xml,.yml,.yaml"
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      </Paper>
      {/* --- Modale Explorer améliorée --- */}
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 22, fontWeight: 600 }}>Ouvrir un fichier (explorer)</DialogTitle>
        <DialogContent sx={{ bgcolor: '#f7f9fa', p: 3 }}>
          <Box mb={2} display="flex" gap={2} alignItems="center">
            <Button size="medium" onClick={handleExplorerGoUp} disabled={explorerPath === '/'}>Remonter</Button>
            <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 16, flex: 1 }}>
              <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }} onClick={() => { setExplorerPath('/'); fetchExplorerFiles('/'); }}>Racine</span>
              {explorerPath.split('/').filter(Boolean).map((part, idx, arr) => {
                const path = '/' + arr.slice(0, idx + 1).join('/');
                return (
                  <span
                    key={idx}
                    style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                    onClick={() => { setExplorerPath(path); fetchExplorerFiles(path); }}
                  >{part}</span>
                );
              })}
            </Breadcrumbs>
            <IconButton onClick={() => handleExplorerNew('folder')} title="Nouveau dossier" size="large"><AddIcon fontSize="medium" /></IconButton>
            <IconButton onClick={() => handleExplorerNew('file')} title="Nouveau fichier" size="large"><InsertDriveFileIcon fontSize="medium" /></IconButton>
          </Box>
          {explorerLoading ? <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}><CircularProgress /></Box> : (
            <Box sx={{ border: '2px solid #b3c6e0', borderRadius: 2, bgcolor: '#fafdff', p: 1, boxShadow: 1, maxHeight: 370, overflowY: 'auto', mt: 1 }}>
              <List sx={{ fontSize: 18 }}>
                {explorerFiles.map((file, idx) => (
                  <ListItem
                    button
                    key={idx}
                    onDoubleClick={() => file.type === 'folder' ? handleExplorerNavigate(file.name) : handleExplorerSelectFile(file)}
                    sx={{ py: 2, borderRadius: 2, mb: 1, '&:hover': { bgcolor: '#e3f2fd' } }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {file.type === 'folder' ? <FolderIcon color="primary" fontSize="medium" /> : <InsertDriveFileIcon fontSize="medium" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontSize: 18, fontWeight: file.type === 'folder' ? 600 : 400, color: file.type === 'folder' ? '#1976d2' : '#222' }}>{file.name}</span>}
                      secondary={file.type === 'folder' ? 'Dossier' : 'Fichier'}
                      onClick={file.type === 'folder' ? () => handleExplorerNavigate(file.name) : undefined}
                      style={file.type === 'folder' ? { cursor: 'pointer', color: '#1976d2' } : {}}
                    />
                  </ListItem>
                ))}
                {explorerFiles.length === 0 && <ListItem><ListItemText primary={<span style={{ fontSize: 17 }}>Dossier vide</span>} /></ListItem>}
              </List>
            </Box>
          )}
          {explorerError && <Typography color="error" sx={{ mt: 2, fontSize: 16 }}>{explorerError}</Typography>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExplorerOpen(false)} size="large">Annuler</Button>
        </DialogActions>
      </Dialog>
      {/* --- Dialog création fichier/dossier --- */}
      <Dialog open={explorerNewDialog.open} onClose={handleExplorerNewCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 20 }}>Créer un {explorerNewDialog.type === 'folder' ? 'dossier' : 'fichier'}</DialogTitle>
        <DialogContent>
          <TextField label={explorerNewDialog.type === 'folder' ? 'Nom du dossier' : 'Nom du fichier'} value={explorerNewDialog.value} onChange={(e) => setExplorerNewDialog((d) => ({ ...d, value: e.target.value }))} fullWidth size="medium" sx={{ fontSize: 18, mt: 1 }} InputLabelProps={{ style: { fontSize: 16 } }} InputProps={{ style: { fontSize: 18 } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExplorerNewCancel} size="large">Annuler</Button>
          <Button onClick={handleExplorerNewValidate} variant="contained" size="large">Créer</Button>
        </DialogActions>
      </Dialog>
      {/* --- Dialog sauvegarde améliorée --- */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 20 }}>Enregistrer sur l'explorer</DialogTitle>
        <DialogContent sx={{ bgcolor: '#f7f9fa', p: 3 }}>
          <Box mb={2} display="flex" gap={2} alignItems="center">
            <Button size="medium" onClick={() => setSavePath((prev) => {
              if (prev === '/') return '/';
              const parts = prev.split('/').filter(Boolean);
              parts.pop();
              return parts.length === 0 ? '/' : '/' + parts.join('/');
            })} disabled={savePath === '/'}>Remonter</Button>
            <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 16, flex: 1 }}>
              <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }} onClick={() => setSavePath('/')}>Racine</span>
              {savePath.split('/').filter(Boolean).map((part, idx, arr) => {
                const path = '/' + arr.slice(0, idx + 1).join('/');
                return (
                  <span
                    key={idx}
                    style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                    onClick={() => setSavePath(path)}
                  >{part}</span>
                );
              })}
            </Breadcrumbs>
            <IconButton onClick={() => handleSaveExplorerNew('folder')} title="Nouveau dossier" size="large"><AddIcon fontSize="medium" /></IconButton>
          </Box>
          <Box sx={{ border: '2px solid #b3c6e0', borderRadius: 2, bgcolor: '#fafdff', p: 1, boxShadow: 1, maxHeight: 270, overflowY: 'auto', mt: 1, mb: 2 }}>
            <List sx={{ fontSize: 18 }}>
              {saveExplorerFiles.map((file, idx) => (
                <ListItem
                  button={file.type === 'folder'}
                  key={idx}
                  onDoubleClick={file.type === 'folder' ? () => handleSaveExplorerNavigate(file.name) : undefined}
                  sx={{ py: 2, borderRadius: 2, mb: 1, '&:hover': { bgcolor: file.type === 'folder' ? '#e3f2fd' : undefined } }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {file.type === 'folder' ? <FolderIcon color="primary" fontSize="medium" /> : <InsertDriveFileIcon fontSize="medium" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={<span style={{ fontSize: 18, fontWeight: file.type === 'folder' ? 600 : 400, color: file.type === 'folder' ? '#1976d2' : '#222' }}>{file.name}</span>}
                    secondary={file.type === 'folder' ? 'Dossier' : 'Fichier'}
                    onClick={file.type === 'folder' ? () => handleSaveExplorerNavigate(file.name) : undefined}
                    style={file.type === 'folder' ? { cursor: 'pointer', color: '#1976d2' } : {}}
                  />
                </ListItem>
              ))}
              {(!saveExplorerFiles || saveExplorerFiles.length === 0) && <ListItem><ListItemText primary={<span style={{ fontSize: 17 }}>Dossier vide</span>} /></ListItem>}
            </List>
          </Box>
          <TextField label="Nom du fichier" value={saveName} onChange={(e) => setSaveName(e.target.value)} fullWidth size="medium" sx={{ fontSize: 18, mb: 1 }} InputLabelProps={{ style: { fontSize: 16 } }} InputProps={{ style: { fontSize: 18 } }} />
          {saveError && <Typography color="error" sx={{ mt: 2, fontSize: 16 }}>{saveError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} size="large">Annuler</Button>
          <Button onClick={handleSaveDialogValidate} disabled={saveLoading} variant="contained" size="large">Valider</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
